import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, ClipboardList, Download } from "lucide-react";
import { api, ApiError, getAccessToken } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/ui/Toast";
import type { Assignment, AssignmentSubmission } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

async function downloadSubmissionFile(submissionId: string) {
  const res = await fetch(`/api/v1.0/submissions/${submissionId}/download`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
  });
  if (!res.ok) throw new Error("Failed to download file");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "submission";
  a.click();
  URL.revokeObjectURL(url);
}

function StudentSubmissionForm({ assignmentId }: { assignmentId: string }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const { data: mySubmission, isLoading } = useQuery({
    queryKey: ["assignments", assignmentId, "submissions", "me"],
    queryFn: () => api.get<AssignmentSubmission>(`/api/v1.0/assignments/${assignmentId}/submissions/me`),
    retry: false,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append("notes", notes);
      if (file) form.append("file", file);
      return api.postForm(`/api/v1.0/assignments/${assignmentId}/submit`, form);
    },
    onSuccess: () => {
      notify("Assignment submitted");
      void queryClient.invalidateQueries({ queryKey: ["assignments", assignmentId, "submissions", "me"] });
      // Submitting can unlock the next lesson in the course sequence.
      void queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
    onError: (err) => notify(err instanceof ApiError ? err.message : "Failed to submit", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate();
  };

  if (isLoading) return <FullPageSpinner />;

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        {mySubmission && (
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <Badge tone={statusTone(mySubmission.status)}>{mySubmission.status}</Badge>
              {mySubmission.score != null && <span className="text-sm font-medium text-slate-700">Score: {mySubmission.score}</span>}
            </div>
            {mySubmission.feedback && <p className="text-sm text-slate-600">Feedback: {mySubmission.feedback}</p>}
            <p className="mt-1 text-xs text-slate-400">
              Last submitted {new Date(mySubmission.submitted_at).toLocaleString()}
            </p>
          </div>
        )}
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Notes</label>
            <textarea
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
          <Button type="submit" loading={isPending} className="self-start">
            <Upload className="h-4 w-4" /> {mySubmission ? "Resubmit" : "Submit"}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function GradingRow({ submission, assignmentId }: { submission: AssignmentSubmission; assignmentId: string }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [score, setScore] = useState(submission.score?.toString() ?? "");
  const [feedback, setFeedback] = useState(submission.feedback ?? "");

  const { mutate, isPending } = useMutation({
    mutationFn: () => api.patch(`/api/v1.0/submissions/${submission.id}/grade`, { score: Number(score), feedback }),
    onSuccess: () => {
      notify("Submission graded");
      void queryClient.invalidateQueries({ queryKey: ["assignments", assignmentId, "submissions"] });
    },
  });

  return (
    <div className="flex flex-col gap-2 border-b border-slate-100 py-4 last:border-0">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-slate-800">{submission.student_name}</p>
          <p className="text-xs text-slate-400">{submission.student_email}</p>
        </div>
        <Badge tone={statusTone(submission.status)}>{submission.status}</Badge>
      </div>
      {submission.notes && <p className="text-sm text-slate-600">{submission.notes}</p>}
      {submission.file_url && (
        <button
          onClick={() => downloadSubmissionFile(submission.id).catch(() => notify("Failed to download file", "error"))}
          className="flex w-fit items-center gap-1 text-sm text-brand-600 hover:underline"
        >
          <Download className="h-3.5 w-3.5" /> Download submitted file
        </button>
      )}
      <div className="flex items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">Score</label>
          <input
            type="number"
            className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={score}
            onChange={(e) => setScore(e.target.value)}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-xs text-slate-500">Feedback</label>
          <input
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </div>
        <Button size="sm" loading={isPending} onClick={() => mutate()}>
          Save grade
        </Button>
      </div>
    </div>
  );
}

function StaffSubmissions({ assignmentId }: { assignmentId: string }) {
  const { data: submissions, isLoading } = useQuery({
    queryKey: ["assignments", assignmentId, "submissions"],
    queryFn: () => api.get<AssignmentSubmission[]>(`/api/v1.0/assignments/${assignmentId}/submissions`),
  });

  if (isLoading) return <FullPageSpinner />;
  if (!submissions?.length) {
    return <EmptyState icon={ClipboardList} title="No submissions yet" />;
  }

  return (
    <Card>
      <CardBody>
        {submissions.map((s) => (
          <GradingRow key={s.id} submission={s} assignmentId={assignmentId} />
        ))}
      </CardBody>
    </Card>
  );
}

export function AssignmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "instructor";

  const { data: assignment, isLoading } = useQuery({
    queryKey: ["assignments", id],
    queryFn: () => api.get<Assignment>(`/api/v1.0/assignments/${id}`),
    enabled: !!id,
  });

  usePageTitle(assignment?.title);

  if (isLoading || !assignment) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <div>
        {assignment.instructions && <p className="text-sm text-slate-500">{assignment.instructions}</p>}
        <p className="mt-1 text-xs text-slate-400">
          Max score {assignment.max_score}
          {assignment.due_at && <> · Due {new Date(assignment.due_at).toLocaleString()}</>}
        </p>
      </div>

      {isStaff ? <StaffSubmissions assignmentId={id!} /> : <StudentSubmissionForm assignmentId={id!} />}
    </div>
  );
}
